import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionService, useSubscriptionStatus } from '../services/subscriptionService';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Chip,
  Paper,
  Avatar,
  Stack,
  Divider,
  LinearProgress,
  Zoom,
  Fade,
  Grow,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  Star,
  LocalFireDepartment,
  FlashOn,
  EmojiEvents,
  Diamond,
  MonetizationOn,
  AutoAwesome,
  Celebration,
  Bolt,
  Rocket,
  TrendingUp,
  Favorite,
  CheckCircle,
  CardGiftcard,
  AllInclusive
} from '@mui/icons-material';

const GetTokens = () => {
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState('best'); // Default to best offer
  const [showCelebration, setShowCelebration] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { subscriptionStatus, tokenInfo } = useSubscriptionStatus();

  const packages = [
    {
      id: 'starter',
      name: 'Starter Pack',
      tokens: 100,
      price: 8,
      pricePerToken: 0.08,
      color: '#2196F3',
      gradient: 'linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)',
      icon: <FlashOn sx={{ fontSize: 40 }} />,
      description: 'Perfect for getting started',
      features: ['100 AI Conversations', 'Basic Support', 'Mobile Access'],
      savings: null
    },
    {
      id: 'popular',
      name: 'Popular Choice',
      tokens: 250,
      price: 15,
      pricePerToken: 0.06,
      color: '#FF9800',
      gradient: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
      icon: <Bolt sx={{ fontSize: 40 }} />,
      description: 'Most popular option',
      features: ['250 AI Conversations', 'Priority Support', 'Advanced Features'],
      savings: 'Save 25%'
    },
    {
      id: 'best',
      name: 'Best Value',
      tokens: 500,
      price: 25,
      pricePerToken: 0.05,
      color: '#4CAF50',
      gradient: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
      icon: <Rocket sx={{ fontSize: 40 }} />,
      description: 'Maximum value & savings',
      features: ['500 AI Conversations', 'VIP Support', 'All Premium Features', 'Bonus Wellness Content', 'Tokens accumulate forever'],
      savings: 'Save 37.5%',
      badge: 'BEST VALUE',
      popular: true
    }
  ];

  const handlePackageSelect = (packageId) => {
    setSelectedPackage(packageId);
    setShowCelebration(true);
    setTimeout(() => setShowCelebration(false), 1500);
  };

  const handlePurchase = async () => {
    const selectedPkg = packages.find(pkg => pkg.id === selectedPackage);
    setLoading(true);
    setError(null);
    
    try {
      // Use the subscription service to create a token purchase
      const result = await subscriptionService.createTokenPurchase(selectedPkg.id);
      
      // The service will automatically redirect to Stripe Checkout
      // If we reach here, there might be an issue
      if (!result.sessionUrl) {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      setError(error.message || 'Failed to process purchase. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPackageElevation = (packageId) => {
    return selectedPackage === packageId ? 8 : 2;
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 3 }, px: { xs: 1, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: { xs: 2, sm: 4 },
        gap: { xs: 1, sm: 2 },
        px: { xs: 1, sm: 0 }
      }}>
        <IconButton 
          onClick={() => navigate('/conversation')} 
          sx={{ 
            mr: { xs: 0.5, sm: 2 },
            width: { xs: 36, sm: 48 },
            height: { xs: 36, sm: 48 }
          }}
        >
          <ArrowBack sx={{ fontSize: { xs: 18, sm: 24 } }} />
        </IconButton>
        <MonetizationOn sx={{ color: '#FFD700', fontSize: { xs: 20, sm: 32 } }} />
        <Typography variant="h4" sx={{ 
          fontWeight: 'bold', 
          color: 'primary.main',
          fontSize: { xs: '1.25rem', sm: '2rem', md: '2.125rem' }
        }}>
          Get More Tokens
        </Typography>
      </Box>

      {/* Hero Section - Mobile Optimized */}
      <Paper 
        sx={{ 
          p: { xs: 2, sm: 4 }, 
          mb: { xs: 2, sm: 4 }, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          textAlign: 'center',
          borderRadius: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 'bold', 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.25rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
            lineHeight: { xs: 1.3, sm: 1.3 }
          }}>
            ✨ Power Up Your Wellness! ✨
          </Typography>
          <Typography variant="h6" sx={{ 
            mb: { xs: 1.5, sm: 3 }, 
            opacity: 0.9,
            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem', lg: '1.25rem' },
            px: { xs: 0.5, sm: 0 }
          }}>
            Tokens that accumulate forever!
          </Typography>
          <Stack 
            direction={{ xs: 'row', sm: 'row' }}
            spacing={{ xs: 0.5, sm: 2 }}
            justifyContent="center" 
            sx={{ 
              mb: 2,
              alignItems: 'center',
              flexWrap: 'wrap'
            }}
          >
            <Chip 
              icon={<Star sx={{ color: '#FFD700 !important' }} />}
              label="Instant"
              size="small"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontSize: { xs: '0.6875rem', sm: '0.875rem' },
                pointerEvents: 'none'
              }}
            />
            <Chip 
              icon={<LocalFireDepartment sx={{ color: '#FF6B6B !important' }} />}
              label="Forever"
              size="small"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontSize: { xs: '0.6875rem', sm: '0.875rem' },
                pointerEvents: 'none'
              }}
            />
            <Chip 
              icon={<EmojiEvents sx={{ color: '#FFD700 !important' }} />}
              label="Best Price"
              size="small"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontSize: { xs: '0.6875rem', sm: '0.875rem' },
                pointerEvents: 'none'
              }}
            />
          </Stack>
        </Box>
        
        {/* Animated background elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        }} />
      </Paper>

      {/* Best Value Highlight - Mobile Optimized */}
      <Box sx={{ mb: { xs: 2, sm: 4 }, textAlign: 'center' }}>
        <Paper 
          sx={{ 
            p: { xs: 1.5, sm: 3 }, 
            background: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
            color: 'white',
            borderRadius: { xs: 2, sm: 3 },
            boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
            border: { xs: '2px solid #FFD700', sm: '3px solid #FFD700' },
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Typography variant="h5" sx={{ 
            fontWeight: 'bold', 
            mb: { xs: 0.5, sm: 1 },
            fontSize: { xs: '1rem', sm: '1.5rem' }
          }}>
            🌟 BEST VALUE: 500 Tokens for $25 🌟
          </Typography>
          <Typography variant="body1" sx={{ 
            mb: { xs: 1, sm: 2 },
            opacity: 0.95,
            fontSize: { xs: '0.8125rem', sm: '1rem' },
            px: { xs: 0.5, sm: 0 }
          }}>
            Save 37.5% compared to buying individually!
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: { xs: 1, sm: 2 }, 
            flexWrap: 'wrap' 
          }}>
            <Chip 
              label="$0.05/token"
              icon={<Star />}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 'bold',
                fontSize: { xs: '0.6875rem', sm: '0.875rem' },
                pointerEvents: 'none'
              }} 
            />
            <Chip 
              label="Never expire"
              icon={<AllInclusive />}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 'bold',
                fontSize: { xs: '0.6875rem', sm: '0.875rem' },
                pointerEvents: 'none'
              }} 
            />
          </Box>
        </Paper>
      </Box>

      {/* Mobile Quick Compare - Only on mobile */}
      <Box sx={{ 
        display: { xs: 'block', sm: 'none' }, 
        mb: 2,
        px: 1
      }}>
        <Paper sx={{ 
          p: 1.5, 
          borderRadius: 2,
          bgcolor: 'rgba(103, 58, 183, 0.05)',
          border: '1px solid rgba(103, 58, 183, 0.1)'
        }}>
          <Typography variant="subtitle2" sx={{ 
            mb: 1, 
            fontWeight: 'bold',
            textAlign: 'center',
            fontSize: '0.875rem'
          }}>
            📊 Quick Compare
          </Typography>
          <Grid container spacing={0.5}>
            {packages.map((pkg) => (
              <Grid item xs={4} key={pkg.id}>
                <Box 
                  onClick={() => handlePackageSelect(pkg.id)}
                  sx={{
                    textAlign: 'center',
                    p: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: selectedPackage === pkg.id ? `2px solid ${pkg.color}` : '1px solid #e0e0e0',
                    bgcolor: selectedPackage === pkg.id ? `${pkg.color}15` : 'white',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Typography variant="caption" sx={{ 
                    fontWeight: 'bold',
                    fontSize: '0.6875rem',
                    color: selectedPackage === pkg.id ? pkg.color : 'text.secondary'
                  }}>
                    {pkg.tokens} tokens
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    fontSize: '0.9375rem',
                    fontWeight: 'bold',
                    color: selectedPackage === pkg.id ? pkg.color : 'text.primary'
                  }}>
                    ${pkg.price}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    fontSize: '0.6875rem',
                    color: 'text.secondary'
                  }}>
                    ${pkg.pricePerToken.toFixed(3)}/ea
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>

      {/* Token Packages - Mobile Optimized */}
      <Grid container spacing={{ xs: 1.5, sm: 3 }} sx={{ mb: { xs: 2, sm: 4 } }}>
        {packages.map((pkg, index) => (
          <Grid item xs={12} sm={6} md={4} key={pkg.id}>
            <Grow in timeout={500 + index * 200}>
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease-in-out',
                  elevation: getPackageElevation(pkg.id),
                  border: selectedPackage === pkg.id ? `3px solid ${pkg.color}` : 'none',
                  transform: selectedPackage === pkg.id ? 'scale(1.02)' : 'scale(1)',
                  overflow: 'visible',
                  mt: pkg.badge ? { xs: 1, sm: 2 } : 0,
                  '&:hover': {
                    transform: selectedPackage === pkg.id ? 'scale(1.02)' : 'scale(1.01)',
                    elevation: 6
                  }
                }}
                onClick={() => handlePackageSelect(pkg.id)}
              >
                {/* Best Offer Badge */}
                {pkg.badge && (
                  <Box sx={{
                    position: 'absolute',
                    top: { xs: -8, sm: -15 },
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 2
                  }}>
                    <Chip
                      label={pkg.badge}
                      size="small"
                      sx={{
                        background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: { xs: '0.6875rem', sm: '0.85rem' },
                        height: { xs: 24, sm: 32 },
                        px: { xs: 0.5, sm: 1 },
                        pointerEvents: 'none',
                        boxShadow: '0 2px 8px rgba(255, 107, 107, 0.3)',
                        '& .MuiChip-label': {
                          px: { xs: 1, sm: 2 },
                          py: 0.5
                        }
                      }}
                      icon={<Star sx={{ color: 'white !important', fontSize: { xs: 14, sm: 18 } }} />}
                    />
                  </Box>
                )}

                <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                  {/* Package Icon */}
                  <Box sx={{ 
                    background: pkg.gradient,
                    borderRadius: '50%',
                    width: { xs: 50, sm: 80 },
                    height: { xs: 50, sm: 80 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: { xs: 1, sm: 2 },
                    color: 'white',
                    '& svg': {
                      fontSize: { xs: 24, sm: 40 }
                    }
                  }}>
                    {pkg.icon}
                  </Box>

                  {/* Package Name */}
                  <Typography variant="h5" sx={{ 
                    fontWeight: 'bold', 
                    mb: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
                  }}>
                    {pkg.name}
                  </Typography>

                  {/* Tokens Count */}
                  <Typography variant="h3" sx={{ 
                    fontWeight: 'bold', 
                    color: pkg.color,
                    mb: { xs: 0.25, sm: 1 },
                    fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3rem' }
                  }}>
                    {pkg.tokens.toLocaleString()}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ 
                    color: 'text.secondary', 
                    mb: { xs: 1, sm: 2 },
                    fontSize: { xs: '0.8125rem', sm: '1rem' }
                  }}>
                    Tokens
                  </Typography>

                  {/* Price */}
                  <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 'bold', 
                      color: 'primary.main',
                      fontSize: { xs: '1.375rem', sm: '2rem', md: '2.125rem' }
                    }}>
                      ${pkg.price}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary',
                      fontSize: { xs: '0.6875rem', sm: '0.875rem' }
                    }}>
                      ${pkg.pricePerToken.toFixed(3)} per token
                    </Typography>
                  </Box>

                  {/* Savings Badge */}
                  {pkg.savings && (
                    <Chip
                      label={pkg.savings}
                      color="success"
                      size="small"
                      sx={{ 
                        mb: { xs: 1, sm: 2 }, 
                        fontWeight: 'bold',
                        fontSize: { xs: '0.6875rem', sm: '0.875rem' },
                        pointerEvents: 'none'
                      }}
                    />
                  )}

                  {/* Description */}
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    mb: { xs: 1, sm: 2 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    {pkg.description}
                  </Typography>

                  {/* Features - Compact for mobile */}
                  <Box sx={{ textAlign: 'left', mb: { xs: 1, sm: 2 } }}>
                    {pkg.features.slice(0, 3).map((feature, idx) => (
                      <Box key={idx} sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: { xs: 0.25, sm: 1 }
                      }}>
                        <CheckCircle sx={{ 
                          color: pkg.color, 
                          mr: { xs: 0.5, sm: 1 }, 
                          fontSize: { xs: 14, sm: 20 }
                        }} />
                        <Typography variant="body2" sx={{ 
                          fontSize: { xs: '0.6875rem', sm: '0.875rem' }
                        }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                    {pkg.features.length > 3 && (
                      <Typography variant="caption" sx={{ 
                        color: 'text.secondary',
                        fontSize: { xs: '0.625rem', sm: '0.75rem' },
                        fontStyle: 'italic'
                      }}>
                        +{pkg.features.length - 3} more features...
                      </Typography>
                    )}
                  </Box>

                  {/* Selection Indicator */}
                  {selectedPackage === pkg.id && (
                    <Zoom in>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        mt: { xs: 0.5, sm: 2 }
                      }}>
                        <CheckCircle sx={{ 
                          color: pkg.color, 
                          mr: { xs: 0.5, sm: 1 },
                          fontSize: { xs: 16, sm: 24 }
                        }} />
                        <Typography sx={{ 
                          color: pkg.color, 
                          fontWeight: 'bold',
                          fontSize: { xs: '0.8125rem', sm: '1rem' }
                        }}>
                          Selected!
                        </Typography>
                      </Box>
                    </Zoom>
                  )}
                </CardContent>
              </Card>
            </Grow>
          </Grid>
        ))}
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: { xs: 2, sm: 3 }, mx: { xs: 1, sm: 0 } }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Current Token Info */}
      {tokenInfo && (
        <Paper sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          mb: { xs: 2, sm: 3 }, 
          backgroundColor: 'rgba(103, 58, 183, 0.1)',
          mx: { xs: 1, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <Star sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 24 } }} />
            <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Current balance: <strong>{tokenInfo.balance} tokens</strong>
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Purchase Button - Mobile Optimized */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 0 },
        position: 'sticky',
        bottom: { xs: 16, sm: 'auto' },
        zIndex: 10,
        background: { xs: 'linear-gradient(to top, rgba(255,255,255,1) 60%, rgba(255,255,255,0) 100%)', sm: 'none' },
        pt: { xs: 2, sm: 0 },
        pb: { xs: 1, sm: 0 }
      }}>
        <Button
          onClick={handlePurchase}
          variant="contained"
          size="large"
          disabled={loading}
          sx={{
            background: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
            color: 'white',
            fontSize: { xs: '0.9375rem', sm: '1.2rem' },
            fontWeight: 'bold',
            py: { xs: 1.25, sm: 2 },
            px: { xs: 2.5, sm: 6 },
            borderRadius: { xs: 2, sm: 3 },
            boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
            minWidth: { xs: 200, sm: 'auto' },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '100%', sm: 'none' },
            '&:hover': {
              background: 'linear-gradient(135deg, #45a049 0%, #7cb342 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
            },
            transition: 'all 0.3s ease-in-out'
          }}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CardGiftcard sx={{ fontSize: { xs: 18, sm: 24 } }} />}
        >
          <Box sx={{ 
            display: { xs: 'block', sm: 'inline' },
            lineHeight: { xs: 1.2, sm: 1 }
          }}>
            <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
              {loading ? 'Processing...' : `Get ${packages.find(pkg => pkg.id === selectedPackage)?.tokens} Tokens`}
            </Box>
            {!loading && (
              <Box component="span" sx={{ 
                display: { xs: 'block', sm: 'inline' },
                fontSize: { xs: '0.8125rem', sm: 'inherit' }
              }}>
                {' '}for ${packages.find(pkg => pkg.id === selectedPackage)?.price}
              </Box>
            )}
          </Box>
        </Button>
      </Box>

      {/* Benefits Section - Mobile Optimized */}
      <Paper sx={{ 
        p: { xs: 1.5, sm: 3 }, 
        borderRadius: { xs: 2, sm: 3 }, 
        bgcolor: 'background.default',
        mx: { xs: 1, sm: 0 }
      }}>
        <Typography variant="h6" sx={{ 
          mb: { xs: 1.5, sm: 3 }, 
          textAlign: 'center', 
          fontWeight: 'bold',
          fontSize: { xs: '1rem', sm: '1.25rem' }
        }}>
          🎯 Why Choose Our Tokens?
        </Typography>
        <Grid container spacing={{ xs: 1, sm: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Box sx={{ 
              textAlign: 'center', 
              p: { xs: 0.5, sm: 2 }
            }}>
              <LocalFireDepartment sx={{ 
                fontSize: { xs: 28, sm: 40 }, 
                color: '#FF6B6B', 
                mb: { xs: 0.5, sm: 1 }
              }} />
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                mb: { xs: 0.5, sm: 1 },
                fontSize: { xs: '0.8125rem', sm: '1rem' }
              }}>
                Instant Access
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'text.secondary',
                fontSize: { xs: '0.6875rem', sm: '0.875rem' },
                lineHeight: { xs: 1.3, sm: 1.5 }
              }}>
                Tokens activate immediately
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Box sx={{ 
              textAlign: 'center', 
              p: { xs: 0.5, sm: 2 }
            }}>
              <Diamond sx={{ 
                fontSize: { xs: 28, sm: 40 }, 
                color: '#9C27B0', 
                mb: { xs: 0.5, sm: 1 }
              }} />
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                mb: { xs: 0.5, sm: 1 },
                fontSize: { xs: '0.8125rem', sm: '1rem' }
              }}>
                Premium Quality
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'text.secondary',
                fontSize: { xs: '0.6875rem', sm: '0.875rem' },
                lineHeight: { xs: 1.3, sm: 1.5 }
              }}>
                High-quality AI responses
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Box sx={{ 
              textAlign: 'center', 
              p: { xs: 0.5, sm: 2 }
            }}>
              <TrendingUp sx={{ 
                fontSize: { xs: 28, sm: 40 }, 
                color: '#4CAF50', 
                mb: { xs: 0.5, sm: 1 }
              }} />
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                mb: { xs: 0.5, sm: 1 },
                fontSize: { xs: '0.8125rem', sm: '1rem' }
              }}>
                Best Value
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'text.secondary',
                fontSize: { xs: '0.6875rem', sm: '0.875rem' },
                lineHeight: { xs: 1.3, sm: 1.5 }
              }}>
                More tokens, better savings
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Box sx={{ 
              textAlign: 'center', 
              p: { xs: 0.5, sm: 2 }
            }}>
              <Favorite sx={{ 
                fontSize: { xs: 28, sm: 40 }, 
                color: '#E91E63', 
                mb: { xs: 0.5, sm: 1 }
              }} />
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                mb: { xs: 0.5, sm: 1 },
                fontSize: { xs: '0.8125rem', sm: '1rem' }
              }}>
                Wellness Focus
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'text.secondary',
                fontSize: { xs: '0.6875rem', sm: '0.875rem' },
                lineHeight: { xs: 1.3, sm: 1.5 }
              }}>
                Dedicated to your mental health
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Celebration Animation */}
      {showCelebration && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: 9999
        }}>
          <Fade in timeout={300}>
            <Box sx={{
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 3,
              p: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}>
              <Celebration sx={{ fontSize: 60, color: '#FFD700', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                Great Choice! 🎉
              </Typography>
            </Box>
          </Fade>
        </Box>
      )}
    </Container>
  );
};

export default GetTokens;
